import { parseUtcTimestamp } from '../../components/helpers/timeSince';
import {
    NOTIFICATION_KEYS,
    NOTIFICATION_SEVERITY,
    NOTIFICATION_TYPES,
} from './notificationTypes';

const DAY_MS = 24 * 60 * 60 * 1000;
export const INACTIVE_ADMIN_DAYS = 90;
export const API_KEY_EXPIRY_WARNING_DAYS = 30;

// Status codes, as the Numbers, Users, Trunks and TRS Registrations pages
// label them.
const NUMBER_ACTION_REQUIRED = 7;
const USER_ERROR = 20;
const TRUNK_DEGRADED = 8;
const TRS_ERROR = 20;

const asArray = (value) => (Array.isArray(value) ? value : []);

export const countActionRequiredNumbers = (numbers) =>
    asArray(numbers).filter((n) => n?.status === NUMBER_ACTION_REQUIRED).length;

/** Active admins with no login in the last 90 days, including never. */
export const countInactiveAdmins = (admins, now) =>
    asArray(admins).filter((admin) => {
        if (admin?.isActive === false) return false;
        const lastLogin = parseUtcTimestamp(admin?.lastLogin);
        return (
            lastLogin === null || now - lastLogin > INACTIVE_ADMIN_DAYS * DAY_MS
        );
    }).length;

/** Active API keys already expired or expiring in the next 30 days. */
export const countExpiringApiKeys = (apiUsers, now) =>
    asArray(apiUsers).filter((apiUser) => {
        if (apiUser?.isActive === false) return false;
        const expiration = parseUtcTimestamp(apiUser?.expiration);
        return (
            expiration !== null &&
            expiration - now <= API_KEY_EXPIRY_WARNING_DAYS * DAY_MS
        );
    }).length;

export const countUsersInError = (tcapUsers) =>
    asArray(tcapUsers).filter((user) => user?.status === USER_ERROR).length;

export const countDegradedTrunks = (trunks) =>
    asArray(trunks).filter((trunk) => trunk?.status === TRUNK_DEGRADED).length;

export const countFailedTrsRegistrations = (registrations) =>
    asArray(registrations).filter(
        (registration) =>
            registration?.status === TRS_ERROR ||
            String(registration?.status).toLowerCase() === 'failed',
    ).length;

/**
 * (2635) The one account-wide warning the old company banner showed, checked
 * in the same order: not approved, trial expired, Microsoft sync failing,
 * then deactivated.
 *
 * @param {object} [company] - /Company row
 * @param {object} [msAuth] - the company's Microsoft Teams app auth
 * @param {number} now - epoch ms
 * @returns {string|null} a NOTIFICATION_KEYS value
 */
export const getCompanyStatusKey = (company, msAuth, now) => {
    if (!company) return null;
    if (!company.approved) return NOTIFICATION_KEYS.COMPANY_NOT_APPROVED;

    const trialEnd = parseUtcTimestamp(company.trialEnd);
    if (company.isTrial && trialEnd !== null && trialEnd < now) {
        return NOTIFICATION_KEYS.TRIAL_EXPIRED;
    }

    // The last access test came after the last success, so it failed.
    if (
        Date.parse(msAuth?.lastAccessTest) >
        Date.parse(msAuth?.lastAccessSuccess)
    ) {
        return NOTIFICATION_KEYS.SERVICE_CANNOT_CONNECT;
    }

    if (company.isActive === false) {
        return NOTIFICATION_KEYS.ACCOUNT_DEACTIVATED;
    }
    return null;
};

const SEVERITY_ORDER = [
    NOTIFICATION_SEVERITY.ERROR,
    NOTIFICATION_SEVERITY.WARNING,
];

/**
 * (3153) The warnings listed under the account summary's bell: one per type that
 * has something to report, errors first. Data still loading or that failed
 * to load counts as nothing to report.
 *
 * @param {object} data
 * @param {object[]} [data.numbers] - numbers summary rows
 * @param {object[]} [data.admins] - /AdminUsers rows
 * @param {object[]} [data.apiUsers] - /apiusers rows
 * @param {object[]} [data.tcapUsers] - /TCAPUsers tcapUsers
 * @param {object[]} [data.trunks] - /Trunks rows
 * @param {object[]} [data.trsRegistrations] - /TRSRegistrations rows
 * @param {object} [data.serviceHealth] - what the service sections report
 * @param {object} [data.company] - /Company row
 * @param {object} [data.msAuth] - the company's Microsoft Teams app auth
 * @param {string} [data.legacySyncRetirementDate] - set only while the
 *   company still needs to upgrade off legacy Microsoft sync
 * @param {number} now - epoch ms
 * @returns {object[]} NOTIFICATION_TYPES entries with a `count`
 */
export default function buildNotifications(data, now) {
    const companyStatusKey = getCompanyStatusKey(
        data.company,
        data.msAuth,
        now,
    );
    const counts = {
        ...(companyStatusKey && { [companyStatusKey]: 1 }),
        [NOTIFICATION_KEYS.LEGACY_SYNC_RETIREMENT]:
            data.legacySyncRetirementDate ? 1 : 0,
        [NOTIFICATION_KEYS.NUMBERS_ACTION_REQUIRED]: countActionRequiredNumbers(
            data.numbers,
        ),
        [NOTIFICATION_KEYS.INACTIVE_ADMINS]: countInactiveAdmins(
            data.admins,
            now,
        ),
        [NOTIFICATION_KEYS.API_KEYS_EXPIRING]: countExpiringApiKeys(
            data.apiUsers,
            now,
        ),
        [NOTIFICATION_KEYS.APP_AUTH_FAILURE]:
            data.serviceHealth?.failedAppAuths ?? 0,
        [NOTIFICATION_KEYS.SERVICE_ATTENTION]:
            data.serviceHealth?.servicesNeedingAttention ?? 0,
        [NOTIFICATION_KEYS.USERS_IN_ERROR]: countUsersInError(data.tcapUsers),
        [NOTIFICATION_KEYS.DEGRADED_TRUNKS]: countDegradedTrunks(data.trunks),
        [NOTIFICATION_KEYS.TRS_REGISTRATION_FAILURE]:
            countFailedTrsRegistrations(data.trsRegistrations),
    };

    const details = {
        [NOTIFICATION_KEYS.COMPANY_NOT_APPROVED]:
            data.company?.partnerName &&
            `Services will not provision until your company has been approved. Please contact ${data.company.partnerName} for more information.`,
        [NOTIFICATION_KEYS.LEGACY_SYNC_RETIREMENT]:
            data.legacySyncRetirementDate &&
            `No longer supported after ${data.legacySyncRetirementDate}. Upgrade to prevent service delays and avoid disruption.`,
    };

    return NOTIFICATION_TYPES.map((type) => ({
        ...type,
        detail: details[type.key] || type.detail,
        count: counts[type.key] ?? 0,
    }))
        .filter((notification) => notification.count > 0)
        .sort(
            (a, b) =>
                SEVERITY_ORDER.indexOf(a.severity) -
                SEVERITY_ORDER.indexOf(b.severity),
        );
}
